import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const healthColor = (h) => {
  if (h >= 80) return { text: "text-emerald-600", bg: "bg-emerald-500", label: "Excellent", labelAr: "ممتاز" };
  if (h >= 60) return { text: "text-yellow-600", bg: "bg-yellow-500", label: "Good", labelAr: "جيد" };
  if (h >= 40) return { text: "text-orange-600", bg: "bg-orange-500", label: "Fair", labelAr: "مقبول" };
  return { text: "text-red-600", bg: "bg-red-500", label: "Needs Attention", labelAr: "يحتاج عناية" };
};

const MiniBar = ({ value, color = "bg-emerald-500" }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  return (
    <div ref={ref} className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={inView ? { width: `${Math.min(value, 100)}%` } : { width: 0 }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
    </div>
  );
};

const FieldsSummaryWidget = ({ fields, isArabic }) => {
  if (!fields || fields.length === 0) return null;

  // Compute crop summary
  const cropMap = {};
  let totalHealth = 0;
  let healthCount = 0;
  fields.forEach((f) => {
    if (f.currentCrop) {
      cropMap[f.currentCrop] = (cropMap[f.currentCrop] || 0) + 1;
    }
    if (f.cropHealth != null) {
      totalHealth += f.cropHealth;
      healthCount++;
    }
  });
  const avgHealth = healthCount > 0 ? Math.round(totalHealth / healthCount) : null;
  const cropEntries = Object.entries(cropMap).sort((a, b) => b[1] - a[1]);
  const hc = avgHealth != null ? healthColor(avgHealth) : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden h-full">
      {/* Header */}
      <div className={`px-5 py-4 border-b border-slate-100 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-lg flex-shrink-0">🌾</div>
        <div className={isArabic ? "text-right" : ""}>
          <h3 className="text-sm font-bold text-slate-900 mb-0">
            {isArabic ? "ملخص الحقول" : "Fields Summary"}
          </h3>
          <p className="text-[11px] text-slate-400 mb-0">
            {fields.length} {isArabic ? "حقول نشطة" : "active fields"}
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Avg health + total fields stats row */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700 mb-0">{fields.length}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
              {isArabic ? "إجمالي الحقول" : "Total Fields"}
            </p>
          </div>
          {avgHealth != null && (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-center">
              <p className={`text-2xl font-bold mb-0 ${hc.text}`}>{avgHealth}%</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
                {isArabic ? "متوسط الصحة" : "Avg Health"}
              </p>
            </div>
          )}
        </div>

        {/* Crop breakdown */}
        {cropEntries.length > 0 && (
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 ${isArabic ? "text-right" : ""}`}>
              {isArabic ? "توزيع المحاصيل" : "Crop Breakdown"}
            </p>
            <div className="space-y-2">
              {cropEntries.map(([crop, count]) => (
                <div key={crop} className={`flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
                  <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
                    <span className="text-xs">🌱</span>
                    <span className="text-sm font-medium text-slate-700">{crop}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    {count} {count > 1 ? (isArabic ? "حقول" : "fields") : (isArabic ? "حقل" : "field")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-field mini list */}
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {fields.map((field) => {
            const fhc = field.cropHealth != null ? healthColor(field.cropHealth) : null;
            return (
              <div key={field.id} className="flex items-center gap-3 rounded-lg bg-slate-50 border border-slate-100 p-2.5">
                <div className="flex-1 min-w-0">
                  <div className={`flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
                    <span className="text-xs font-bold text-slate-800 truncate">{field.title}</span>
                    {field.area && (
                      <span className="text-[9px] text-slate-400 font-medium flex-shrink-0 ml-2">{field.area}</span>
                    )}
                  </div>
                  {field.cropHealth != null && (
                    <div className="mt-1.5">
                      <MiniBar value={field.cropHealth} color={fhc.bg} />
                    </div>
                  )}
                </div>
                {field.cropHealth != null && (
                  <span className={`text-[10px] font-bold flex-shrink-0 ${fhc.text}`}>{field.cropHealth}%</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FieldsSummaryWidget;
